import React, { useState } from 'react';
import { MessageSquare, AlertCircle, ChevronDown, ChevronUp, Check, Send } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ApprovalStatus, ReviewComment, Commission } from '../types/commission';
interface ReviewStatusBannerProps {
  status: ApprovalStatus;
  comments: ReviewComment[];
  commission: Commission;
  onSubmitResponse: (message: string) => void;
  onApproveResolution: () => void;
  onSubmitLoanResponse?: (userId: string, loanId: string, message: string) => void;
}
export function ReviewStatusBanner({
  status,
  comments,
  commission,
  onSubmitResponse,
  onApproveResolution,
  onSubmitLoanResponse
}: ReviewStatusBannerProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loanReplies, setLoanReplies] = useState<{
    [loanId: string]: string;
  }>({});
  const flaggedLoans = commission.loans.filter(l => ['flagged', 'under_review', 'resolved_pending_approval'].includes(l.approvalStatus));
  if (flaggedLoans.length === 0) {
    return null;
  }
  const overallStatus = commission.review?.status || 'flagged';
  const handleSubmitLoanReply = (loanId: string) => {
    const message = loanReplies[loanId]?.trim();
    if (message && onSubmitLoanResponse) {
      onSubmitLoanResponse(commission.userId, loanId, message);
      setLoanReplies(prev => ({
        ...prev,
        [loanId]: ''
      }));
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: -20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="mb-6" data-id="element-1189">
      <div className={`rounded-2xl border-2 overflow-hidden ${overallStatus === 'resolved_pending_approval' ? 'bg-emerald-50 border-emerald-200' : overallStatus === 'under_review' ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-200'}`} data-id="element-1190">
        {/* Banner Header */}
        <button onClick={() => setIsExpanded(!isExpanded)} className="w-full px-6 py-4 flex items-center justify-between hover:bg-white/50 transition-colors" data-id="element-1191">
          <div className="flex items-center gap-3" data-id="element-1192">
            <div className={`p-2 rounded-lg ${overallStatus === 'resolved_pending_approval' ? 'bg-emerald-500' : overallStatus === 'under_review' ? 'bg-blue-500' : 'bg-amber-500'}`} data-id="element-1193">
              {overallStatus === 'resolved_pending_approval' ? <Check size={20} className="text-white" data-id="element-1194" /> : overallStatus === 'under_review' ? <MessageSquare size={20} className="text-white" data-id="element-1195" /> : <AlertCircle size={20} className="text-white" data-id="element-1196" />}
            </div>
            <div className="text-left" data-id="element-1197">
              <p className={`text-sm font-bold ${overallStatus === 'resolved_pending_approval' ? 'text-emerald-900' : overallStatus === 'under_review' ? 'text-blue-900' : 'text-amber-900'}`} data-id="element-1198">
                {flaggedLoans.length} Loan{flaggedLoans.length !== 1 ? 's' : ''}{' '}
                Flagged for Review
              </p>
              <p className={`text-xs ${overallStatus === 'resolved_pending_approval' ? 'text-emerald-700' : overallStatus === 'under_review' ? 'text-blue-700' : 'text-amber-700'}`} data-id="element-1199">
                {overallStatus === 'resolved_pending_approval' ? 'Resolution pending approval' : overallStatus === 'under_review' ? 'Admin has responded — reply below' : 'Pending admin review'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2" data-id="element-1200">
            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${overallStatus === 'resolved_pending_approval' ? 'bg-emerald-200 text-emerald-900' : overallStatus === 'under_review' ? 'bg-blue-200 text-blue-900' : 'bg-amber-200 text-amber-900'}`} data-id="element-1201">
              View Details
            </span>
            {isExpanded ? <ChevronUp size={20} className="text-slate-600" data-id="element-1202" /> : <ChevronDown size={20} className="text-slate-600" data-id="element-1203" />}
          </div>
        </button>

        {/* Expanded Content */}
        <AnimatePresence data-id="element-1204">
          {isExpanded && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-1205">
              <div className="px-6 pb-6 space-y-4" data-id="element-1206">
                {flaggedLoans.map(loan => <div key={loan.loanId} className="bg-white rounded-2xl border-2 border-slate-200 overflow-hidden" data-id="element-1207">
                    {/* Loan Header */}
                    <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between" data-id="element-1208">
                      <div className="flex items-center gap-3" data-id="element-1209">
                        <span className="font-bold text-slate-900" data-id="element-1210">
                          {loan.loanId}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${loan.approvalStatus === 'resolved_pending_approval' ? 'bg-emerald-100 text-emerald-700' : loan.approvalStatus === 'under_review' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-1211">
                          {loan.approvalStatus === 'under_review' ? 'Under Review' : loan.approvalStatus === 'resolved_pending_approval' ? 'Resolved' : 'Flagged'}
                        </span>
                      </div>
                      <span className="text-sm font-semibold text-emerald-600 tabular-nums" data-id="element-1212">
                        $
                        {loan.commission.toLocaleString('en-US', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                  })}
                      </span>
                    </div>

                    {/* Comment Thread */}
                    <div className="px-5 py-4" data-id="element-1213">
                      {loan.loanReviewComments && loan.loanReviewComments.length > 0 ? <div className="space-y-2.5 mb-4" data-id="element-1214">
                          {loan.loanReviewComments.map((comment, idx) => <motion.div key={comment.id} initial={{
                    opacity: 0,
                    y: 8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: idx * 0.05
                  }} className={`p-3 rounded-xl text-sm ${comment.authorRole === 'System Administrator' ? 'bg-blue-50 border border-blue-200 ml-6' : 'bg-slate-50 border border-slate-200 mr-6'}`} data-id="element-1215">
                              <div className="flex items-center justify-between mb-1" data-id="element-1216">
                                <div className="flex items-center gap-2" data-id="element-1217">
                                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white ${comment.authorRole === 'System Administrator' ? 'bg-blue-500' : 'bg-slate-500'}`} data-id="element-1218">
                                    {comment.author[0]}
                                  </div>
                                  <span className="font-semibold text-xs text-slate-700" data-id="element-1219">
                                    {comment.author}
                                    {comment.authorRole === 'System Administrator' && <span className="ml-1.5 text-[10px] font-medium text-blue-600" data-id="element-1220">
                                        Admin
                                      </span>}
                                  </span>
                                </div>
                                <span className="text-[10px] text-slate-400" data-id="element-1221">
                                  {comment.timestamp}
                                </span>
                              </div>
                              <p className="text-slate-700 leading-relaxed pl-7" data-id="element-1222">
                                {comment.message}
                              </p>
                            </motion.div>)}
                        </div> : <p className="text-sm text-slate-400 italic mb-4" data-id="element-1223">
                          No comments yet
                        </p>}

                      {/* Resolution Info */}
                      {loan.approvalStatus === 'resolved_pending_approval' && loan.overriddenCommission !== undefined && <div className="mb-4 p-3 bg-emerald-50 rounded-lg border border-emerald-100" data-id="element-1224">
                            <p className="text-sm text-emerald-800 font-medium" data-id="element-1225">
                              Resolution Proposed: Commission adjusted to $
                              {loan.overriddenCommission.toFixed(2)}
                            </p>
                          </div>}

                      {/* Reply Input */}
                      {onSubmitLoanResponse && loan.approvalStatus !== 'resolved_pending_approval' && <div className="flex gap-2" data-id="element-1226">
                            <input type="text" value={loanReplies[loan.loanId] || ''} onChange={e => setLoanReplies(prev => ({
                    ...prev,
                    [loan.loanId]: e.target.value
                  }))} onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmitLoanReply(loan.loanId);
                    }
                  }} placeholder={`Reply to ${loan.loanId}...`} className="flex-1 px-4 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue text-sm bg-white" data-id="element-1227" />
                            <button onClick={() => handleSubmitLoanReply(loan.loanId)} disabled={!loanReplies[loan.loanId]?.trim()} className="px-4 py-2.5 bg-brand-blue hover:bg-brand-blue-light disabled:bg-slate-200 disabled:cursor-not-allowed text-white rounded-xl transition-colors flex items-center gap-1.5" data-id="element-1228">
                              <Send size={14} data-id="element-1229" />
                            </button>
                          </div>}
                    </div>
                  </div>)}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </motion.div>;
}